import React, { useState, useEffect } from "react";
import { useAtom } from "jotai";
import { motion } from "framer-motion";
import { themeAtom, staffDataAtom } from "../../../../-Atoms";

// Firebase imports
import { db, updateDoc, doc } from "../../../../firebaseConfig";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";

// Icons
import ReplayIcon from "@mui/icons-material/Replay";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

import { v4 as uuidv4 } from "uuid";

import UniversalTextField from "../../../../GlobalComponents/UniversalTextField";
import BreakdownItemV2 from "./InvoicingComponents/BreakdownItemV2";
import CateringV2 from "./InvoicingComponents/CateringV2";
import BackgroundRollsV2 from "./InvoicingComponents/BackgroundRollsV2";
import ExtrasV2 from "./InvoicingComponents/ExtrasV2";
import DatesV2 from "./InvoicingComponents/DatesV2";
import StaffV2 from "./InvoicingComponents/StaffV2";
import VideoEditingBreakdownV2 from "./InvoicingComponents/VideoEditingBreakdownV2";
import JobSpecificationsV2 from "./InvoicingComponents/JobSpecificationsV2";
import InvoicingNotesContainer from "../InvoicingNotesContainer";
// TODO: Replace these with V2 components as they're created

const InvoicingTab = ({ jobData, clientData, setHasUnsavedChanges }) => {
  const [theme] = useAtom(themeAtom);
  const [staffData] = useAtom(staffDataAtom);

  // Debounced update function
  const updateFSNow = (field, value, id) => {
    updateDoc(doc(db, "JobData", id), { [field]: value }).catch((error) => {
      console.log(error);
    });
  };

  // State management
  const [imageBreakdown, setImageBreakdown] = useState(
    jobData?.imageBreakdown || []
  );
  const [imageBreakdownClient, setImageBreakdownClient] = useState(
    clientData?.imageBreakdown || []
  );
  const [isTyping] = useState(false);

  // Other states from original component
  const [jobTypeFull, setJobTypeFull] = useState(jobData.jobTypeFull);
  const [retoucher, setRetoucher] = useState(jobData.retoucher || "");
  const [retouchingTime, setRetouchingTime] = useState(jobData.retouchingTime);
  const [bookedStart, setBookedStart] = useState(jobData.bookedStart);
  const [bookedFinish, setBookedFinish] = useState(jobData.bookedFinish);
  const [startTime, setStartTime] = useState(jobData.startTime);
  const [finishTime, setFinishTime] = useState(jobData.finishTime);
  const [overtime, setOvertime] = useState(jobData.overtime);
  const [catering, setCatering] = useState(() => {
    if (Array.isArray(jobData.catering)) return jobData.catering;
    return [];
  });
  const [videoTimeEstimate, setVideoTimeEstimate] = useState(
    jobData.videoTimeEstimate || []
  );
  const [videoNotes, setVideoNotes] = useState(jobData.videoNotes || []);
  const [backgroundRolls, setBackgroundRolls] = useState(() => {
    if (Array.isArray(jobData.backgroundRolls)) return jobData.backgroundRolls;
    return [];
  });
  const [videoEditingBreakdown, setVideoEditingBreakdown] = useState(
    jobData.videoEditingBreakdown
  );
  const [cStrings, setCStrings] = useState(jobData.cStrings);
  const [nippleCovers, setNippleCovers] = useState(jobData.nippleCovers);
  const [printedPages, setPrintedPages] = useState(jobData.printedPages);
  const [operator, setOperator] = useState(jobData.operator);
  const [additionalOperators, setAdditionalOperators] = useState(
    jobData.additionalOperators || []
  );

  // Error states
  const [retoucherError, setRetoucherError] = useState(true);
  const [retouchingTimeError, setRetouchingTimeError] = useState(
    jobTypeFull?.includes("(-V)") || jobTypeFull?.includes("(-E)")
      ? true
      : false
  );

  // Hidden items state
  const [hasHiddenItems, setHasHiddenItems] = useState(false);
  const [showHiddenItems, setShowHiddenItems] = useState(false);
  const [showRemoveBreakdownItem, setShowRemoveBreakdownItem] = useState(false);

  // Modal state
  const [openPostProModal, setOpenPostProModal] = useState(false);
  const [openAddItemDialog, setOpenAddItemDialog] = useState(false);
  const [newItemDescription, setNewItemDescription] = useState("");

  // Total states
  const [totalProcessed, setTotalProcessed] = useState("");
  const [totalExpected, setTotalExpected] = useState("");
  const [totalSupplied, setTotalSupplied] = useState("");

  // Tracks which job the current imageBreakdown state belongs to, to prevent stale writes
  const [imageBreakdownJobId, setImageBreakdownJobId] = useState(jobData.id);

  // Sync with jobData changes
  useEffect(() => {
    setImageBreakdownJobId(jobData.id);
    setImageBreakdown(jobData.imageBreakdown || []);
    setBackgroundRolls(
      Array.isArray(jobData.backgroundRolls) ? jobData.backgroundRolls : []
    );
    setVideoEditingBreakdown(jobData.videoEditingBreakdown);
    setCStrings(jobData.cStrings);
    setNippleCovers(jobData.nippleCovers);
    setPrintedPages(jobData.printedPages);
    setOperator(jobData.operator);
    setAdditionalOperators(jobData.additionalOperators || []);
    setRetoucher(jobData.retoucher || "");
    setRetouchingTime(jobData.retouchingTime);
    setBookedStart(jobData.bookedStart);
    setBookedFinish(jobData.bookedFinish);
    setStartTime(jobData.startTime);
    setFinishTime(jobData.finishTime);
    setOvertime(jobData.overtime);
    setCatering(Array.isArray(jobData.catering) ? jobData.catering : []);
    setJobTypeFull(jobData.jobTypeFull);
    setVideoTimeEstimate(jobData.videoTimeEstimate || []);
    setVideoNotes(jobData.videoNotes || []);
  }, [jobData]);

  // Sync with clientData changes
  useEffect(() => {
    setImageBreakdownClient(clientData?.imageBreakdown || []);
  }, [clientData]);

  // Error validation effects
  useEffect(() => {
    setRetoucherError(!retoucher);
  }, [retoucher]);

  useEffect(() => {
    if (jobTypeFull?.includes("(-V)") || jobTypeFull?.includes("(-E)")) {
      setRetouchingTimeError(!retouchingTime);
    } else {
      setRetouchingTimeError(false);
    }
  }, [retouchingTime, jobTypeFull]);

  // Hidden items effect
  useEffect(() => {
    const hiddenItemsExist = imageBreakdown.some(
      (item) => item.itemHidden === true
    );
    setHasHiddenItems(hiddenItemsExist);
  }, [imageBreakdown]);

  // Calculate totals
  useEffect(() => {
    try {
      let tp = 0;
      let te = 0;
      let ts = 0;

      if (isVideoJob(jobData)) {
        imageBreakdown.forEach((bi) => {
          if (bi.quantityProcessed) {
            tp = parseInt(tp) + parseInt(bi.quantityProcessed);
          }
          if (bi.filesInPost) {
            te = parseInt(te) + parseInt(bi.filesInPost);
          }
          if (bi.quantitySupplied) {
            ts = parseInt(ts) + parseInt(bi.quantitySupplied);
          }
        });
      } else {
        imageBreakdown.forEach((bi) => {
          if (!bi.dontAddToTotalSupplied) {
            if (bi.quantityProcessed) {
              tp = parseInt(tp) + parseInt(bi.quantityProcessed);
              te = parseInt(te) + parseInt(bi.quantityProcessed);
            }
            if (bi.filesInPost) {
              te = parseInt(te) + parseInt(bi.filesInPost);
            }
            if (bi.quantitySupplied) {
              ts = parseInt(ts) + parseInt(bi.quantitySupplied);
            }
          }
        });

        const overAlloc = parseImagesOverAllocation(tp, jobData, clientData);

        // Only write when imageBreakdown is in sync with the current job (prevents
        // stale A-job breakdown totals being written to B-job on tab switch)
        if (imageBreakdownJobId === jobData.id) {
          updateDoc(doc(db, "JobData", jobData.id), {
            imagesProcessedNumbers: tp,
            imagesOverAllocation: overAlloc,
          }).catch((e) => console.log(e));
        }
      }

      // Set totals AFTER all calculations are done
      setTotalProcessed(tp === 0 ? "" : tp);
      setTotalExpected(te === 0 ? "" : te);
      setTotalSupplied(ts === 0 ? "" : ts);
    } catch (e) {
      setTotalProcessed("");
      setTotalExpected("");
      setTotalSupplied("");
    }
  }, [imageBreakdown, clientData, jobData, imageBreakdownJobId]);

  // Event handlers
  const handleValueChange = (e, setValue, kind) => {
    setValue(e.target.value);
    jobData[kind] = e.target.value;
    updateFSNow(kind, e.target.value, jobData.id);
  };

  // Helper function to find staff name - matches expected signature from other components
  const findStaff = (option, staffData) => {
    // Handle external staff objects
    if (option && typeof option === "object" && option.type === "external") {
      return option.name || "External";
    }

    if (option?.firstName && option?.lastName) {
      // If option is already a staff object
      return option.preferredName
        ? `${option.preferredName} ${option.lastName}`
        : `${option.firstName} ${option.lastName}`;
    }

    // If option is a staff ID string
    if (typeof option === "string" && staffData) {
      const staff = staffData.find((s) => s.id === option);
      if (staff) {
        return staff.preferredName
          ? `${staff.preferredName} ${staff.lastName}`
          : `${staff.firstName} ${staff.lastName}`;
      }
    }

    return option || "";
  };

  const handleShowHiddenToggle = (e) => {
    setShowHiddenItems(e.target.checked);
  };

  const handleAddItemClick = () => {
    setOpenAddItemDialog(true);
  };

  const handleAddItemCancel = () => {
    setOpenAddItemDialog(false);
    setNewItemDescription("");
  };

  const handleAddItemConfirm = () => {
    if (!newItemDescription.trim()) {
      return; // Don't create if description is empty
    }

    const newitemObj = {
      id: uuidv4(),
      description: newItemDescription.trim(),
      jobTypesFilter: [],
      quantityProcessed: "",
      filesInPost: "",
      quantitySupplied: "",
      expectedPerHour: "",
      costPerFile: "",
      existingItem: false,
      itemHidden: false,
      kind: "",
    };
    const newBreakdown = [...imageBreakdown, newitemObj];
    setImageBreakdown(newBreakdown);
    updateDoc(doc(db, "JobData", jobData.id), {
      imageBreakdown: newBreakdown,
    }).catch((e) => console.log(e));

    // Reset dialog state
    setOpenAddItemDialog(false);
    setNewItemDescription("");
  };

  const toggleRemoveBreakdownItem = () => {
    setShowRemoveBreakdownItem(true);
    setTimeout(() => {
      setShowRemoveBreakdownItem(false);
    }, 6000);
  };

  const regenerateBreakdownItems = () => {
    const breakdown = [];

    if (clientData?.imageBreakdown) {
      clientData?.imageBreakdown.forEach((bri) => {
        const newitemObj = {
          id: bri.id,
          jobTypesFilter: bri.jobTypesFilter || [],
          description: bri.description,
          dontAddToTotalSupplied: bri.dontAddToTotalSupplied || false,
          quantityProcessed: "",
          filesInPost: "",
          quantitySupplied: "",
          expectedPerHour: bri.expectedPerHour,
          costPerFile: bri.costPerFile,
          existingItem: true,
          itemHidden: false,
        };
        breakdown.push(newitemObj);
      });
    }
    
    // Add items that don't already exist in the current breakdown
    breakdown.forEach((itemA) => {
      if (!imageBreakdown.some((itemB) => itemB.id === itemA.id)) {
        imageBreakdown.push(itemA);
      }
    });

    const updatedBreakdown = [...imageBreakdown];
    setImageBreakdown(updatedBreakdown);
    updateDoc(doc(db, "JobData", jobData.id), {
      imageBreakdown: updatedBreakdown,
    }).catch((e) => console.log(e));
  };

  const handlePostProClose = () => {
    setOpenPostProModal(false);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
  };

  // Style definitions
  const sectionHeaderStyle = {
    fontSize: "1.1rem",
    fontWeight: "bold",
    color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
    marginBottom: 2,
  };

  return (
    <Box sx={{ width: "100%", overflowX: "hidden" }}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}
      >
        {/* Modal */}
        <SeePostProductionModal
          open={openPostProModal}
          handleClose={handlePostProClose}
        />

        {/* Add Item Dialog */}
        <Dialog 
          open={openAddItemDialog} 
          onClose={handleAddItemCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add New Breakdown Item</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Enter a description for the new breakdown item:
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Description"
              fullWidth
              variant="outlined"
              value={newItemDescription}
              onChange={(e) => setNewItemDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  e.preventDefault();
                  handleAddItemConfirm();
                }
              }}
              placeholder="e.g. Ghost / Life / Jackets / T-Shirts etc..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddItemCancel}>Cancel</Button>
            <Button 
              onClick={handleAddItemConfirm} 
              variant="contained"
              disabled={!newItemDescription.trim()}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invoicing Notes */}
        <motion.div variants={itemVariants}>
          <InvoicingNotesContainer
            jobData={jobData}
            clientData={clientData}
            setHasUnsavedChanges={setHasUnsavedChanges}
          />
        </motion.div>

        {/* Job Specifications */}
        <JobSpecificationsV2 jobData={jobData} clientData={clientData} />

        {/* Image/Video Breakdown Section */}
        {(imageBreakdown || jobData.imageBreakdown) && (
          <>
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  borderRadius: "10px",
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(35,35,35,0.7)"
                      : "rgba(235,235,235,0.9)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow:
                    theme === "dark"
                      ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                      : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                  padding: 2,
                  marginBottom: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={sectionHeaderStyle}>
                    {isVideoJob(jobData)
                      ? "Video Breakdown"
                      : "Image Breakdown"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {hasHiddenItems && (
                      <Tooltip title="Show/hide hidden breakdown items">
                        <Switch
                          checked={showHiddenItems}
                          onChange={handleShowHiddenToggle}
                          size="small"
                        />
                      </Tooltip>
                    )}
                    <Tooltip title="Regenerate items from client template">
                      <IconButton
                        onClick={regenerateBreakdownItems}
                        disabled={isTyping}
                        sx={{
                          color:
                            theme === "dark"
                              ? "rgba(255,255,255,0.7)"
                              : "rgba(0,0,0,0.7)",
                        }}
                      >
                        <ReplayIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Show remove buttons (only empty items can be deleted)">
                      <IconButton
                        onClick={toggleRemoveBreakdownItem}
                        disabled={isTyping}
                        sx={{
                          color:
                            theme === "dark"
                              ? "rgba(255,100,100,0.8)"
                              : "rgba(200,50,50,0.8)",
                        }}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Add new breakdown item">
                      <IconButton
                        onClick={handleAddItemClick}
                        disabled={isTyping}
                        sx={{
                          color:
                            theme === "dark"
                              ? "rgba(100,255,100,0.8)"
                              : "rgba(50,150,50,0.8)",
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Totals Display */}
                <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                  <UniversalTextField
                    collectionName="JobData"
                    docId={jobData.id}
                    fieldName="totalProcessed"
                    value={totalProcessed}
                    label={
                      isVideoJob(jobData) ? "Total Shot" : "Total Processed"
                    }
                    disabled
                    adminOnly={false}
                    sx={{ marginTop: 0, flex: "1 1 200px" }}
                  />
                  <UniversalTextField
                    collectionName="JobData"
                    docId={jobData.id}
                    fieldName="totalExpected"
                    value={totalExpected}
                    label={
                      isVideoJob(jobData) ? "Total Edited" : "Expected Supplied"
                    }
                    disabled
                    adminOnly={false}
                    sx={{ marginTop: 0, flex: "1 1 200px" }}
                  />
                  <UniversalTextField
                    collectionName="JobData"
                    docId={jobData.id}
                    fieldName="totalSupplied"
                    value={totalSupplied}
                    label="Total Supplied"
                    disabled
                    adminOnly={false}
                    sx={{ marginTop: 0, flex: "1 1 200px" }}
                  />
                </Box>

                {/* Video specific fields */}
                {isVideoJob(jobData) && (
                  <Box
                    sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}
                  >
                    <UniversalTextField
                      collectionName="JobData"
                      docId={jobData.id}
                      fieldName="videoTimeEstimate"
                      value={videoTimeEstimate}
                      label="Video Time Estimate"
                      multiline
                      rows={1}
                      setHasUnsavedChanges={setHasUnsavedChanges}
                      onCallback={(newValue) => setVideoTimeEstimate(newValue)}
                      sx={{ marginTop: 0, flex: "1 1 200px" }}
                    />
                    <UniversalTextField
                      collectionName="JobData"
                      docId={jobData.id}
                      fieldName="videoNotes"
                      value={videoNotes}
                      label="Video Notes"
                      multiline
                      rows={1}
                      setHasUnsavedChanges={setHasUnsavedChanges}
                      onCallback={(newValue) => setVideoNotes(newValue)}
                      sx={{ marginTop: 0, flex: "1 1 200px" }}
                    />
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Breakdown Items */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {imageBreakdown &&
                    imageBreakdown.map((bi, index) => (
                      <BreakdownItemV2
                        key={bi.id}
                        item={bi}
                        items={imageBreakdown}
                        setItems={setImageBreakdown}
                        index={index}
                        showHiddenItems={showHiddenItems}
                        showRemoveBreakdownItem={showRemoveBreakdownItem}
                        jobData={jobData}
                        imageBreakdownClient={imageBreakdownClient}
                      />
                    ))}
                </Box>
              </Box>
            </motion.div>

            {/* Video Editing Breakdown */}
            {(jobData?.jobTypeFull?.includes("-V") ||
              jobData?.jobTypeFull?.includes("-E")) && (
              <VideoEditingBreakdownV2
                videoEditingBreakdown={videoEditingBreakdown}
                setVideoEditingBreakdown={setVideoEditingBreakdown}
                jobData={jobData}
                staffData={staffData}
                findStaff={findStaff}
              />
            )}

            {/* Catering */}
            <CateringV2
              catering={catering}
              setCatering={setCatering}
              jobData={jobData}
            />

            {/* Background Rolls */}
            <BackgroundRollsV2
              backgroundRolls={backgroundRolls}
              setBackgroundRolls={setBackgroundRolls}
              jobData={jobData}
            />

            {/* Extras */}
            <ExtrasV2
              cStrings={cStrings}
              setCStrings={setCStrings}
              nippleCovers={nippleCovers}
              setNippleCovers={setNippleCovers}
              printedPages={printedPages}
              setPrintedPages={setPrintedPages}
              jobData={jobData}
            />

            {/* Dates */}
            <DatesV2
              bookedStart={bookedStart}
              bookedFinish={bookedFinish}
              startTime={startTime}
              setStartTime={setStartTime}
              jobData={jobData}
              finishTime={finishTime}
              setFinishTime={setFinishTime}
              overtime={overtime}
              setOvertime={setOvertime}
            />

            {/* Staff */}
            <StaffV2
              additionalOperators={additionalOperators}
              staffData={staffData}
              findStaff={findStaff}
              operator={operator}
              setOperator={setOperator}
              jobData={jobData}
              retoucherError={retoucherError}
              jobTypeFull={jobTypeFull}
              retoucher={retoucher}
              setRetoucher={setRetoucher}
              retouchingTimeError={retouchingTimeError}
              retouchingTime={retouchingTime}
              handleValueChange={handleValueChange}
              setRetouchingTime={setRetouchingTime}
            />
          </>
        )}
      </motion.div>
    </Box>
  );
};

// Helper functions
const isVideoJob = (jobData) => {
  if (jobData.jobTypeFull?.includes("-V")) return true;
  if (jobData.jobTypeFull?.includes("-E")) return true;
  return false;
};

const parseImagesOverAllocation = (images, jobData, clientData) => {
  let imageAllocation;
  if (jobData.dayType === "Full Day") {
    if (!isNaN(clientData?.fullDayRate?.images)) {
      imageAllocation = clientData?.fullDayRate?.images;
    } else imageAllocation = 240;
  } else if (jobData.dayType === "Half Day") {
    if (!isNaN(clientData?.halfDayRate?.images)) {
      imageAllocation = clientData?.halfDayRate?.images;
    } else imageAllocation = 120;
  } else if (jobData.creativeDayType === "Full Day") {
    if (!isNaN(clientData?.contentFullDayRate?.images)) {
      imageAllocation = clientData?.contentFullDayRate?.images;
    } else imageAllocation = 240;
  } else if (jobData.creativeDayType === "Half Day") {
    if (!isNaN(clientData?.contentHalfDayRate?.images)) {
      imageAllocation = clientData?.contentHalfDayRate?.images;
    } else imageAllocation = 120;
  } else {
    imageAllocation = 240;
  }
  return images > parseInt(imageAllocation);
};

const SeePostProductionModal = ({ open, handleClose }) => {
  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Add New Breakdown Item</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Please talk to Post Production about adding a new Breakdown Item.
        </DialogContentText>
        <DialogContentText sx={{ marginTop: 2 }}>
          The Breakdown Items are very important to get correct, so we need to
          ensure they aren't being added incorrectly.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} autoFocus>
          Okie Dokie
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicingTab;
